import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/theme.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import FullScreenLoader from './components/FullScreenLoader';
import axiosInstance from './axiosInstance';
import { Container, Navbar, Nav, Button, Card, Row, Col, NavDropdown, Form, Alert, Modal, Table, Tooltip, Dropdown, Spinner } from 'react-bootstrap';
import OverlayTrigger from './components/common/OverlayTrigger';
import { TimesheetProvider } from "./TimeSheetContext";
import { useTimesheetData } from "./TimeSheetContext";
import { FaExpand, FaFileAlt, FaDownload, FaTrash, FaCheck, FaTimes, FaImage, FaTable, 
         FaArrowLeft, FaArrowRight, FaChartBar, FaUserCircle, FaCalendarAlt, FaClock,
         FaHome, FaCog, FaBell, FaQuestionCircle, FaSignOutAlt, FaChevronLeft, FaChevronRight, FaEdit } from 'react-icons/fa';
import { Timesheet } from './models/Timesheet';
import { calculateHours } from './utils/timeUtils';
import { validateRows } from './utils/ValidationUtils';
import { removeFileExtension } from './utils/fileUtils';
import { SettingsProvider } from './contexts/SettingsContext';
import { PermissionsProvider } from './contexts/PermissionsContext';

// Lazy load heavy components for code splitting
const Dashboard = lazy(() => import('./components/Dashboard'));
const InstallPrompt = lazy(() => import('./components/InstallPrompt'));

// Legacy imports - only needed for AppContent (legacy feature)
const FileUpload = lazy(() => import('./components/FileUpload'));
const ImageViewer = lazy(() => import('./components/ImageViewer'));
const ResultsTable = lazy(() => import('./components/ResultsTable').then(m => ({ default: m.ResultsTable })));
const LoadingIndicator = lazy(() => import('./components/LoadingIndicator'));
const ErrorDisplay = lazy(() => import('./components/ErrorDisplay'));
const FullScreenImageModal = lazy(() => import('./components/FullScreenImageModal'));
const EditAllHoursModal = lazy(() => import('./components/EditAllHoursModal'));

// App-level loading fallback
const AppLoadingFallback = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted">טוען את המערכת...</p>
        </div>
    </div>
);

function App() {
    return (
        <AuthProvider>
            <Router>
                <div style={{ minHeight: '100vh' }}>
                    <Suspense fallback={<AppLoadingFallback />}>
                        <Routes>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/*" element={
                                <ProtectedRoute>
                                    <SettingsProvider>
                                        <PermissionsProvider>
                                            <Dashboard />
                                        </PermissionsProvider>
                                    </SettingsProvider>
                                </ProtectedRoute>
                            } />
                        </Routes>
                    </Suspense>
                    <Suspense fallback={null}>
                        <InstallPrompt />
                    </Suspense>
                </div>
            </Router>
        </AuthProvider>
    );
}

function LoginPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // ProtectedRoute bounces unauthenticated visitors here with the ORIGIN in
    // `state.from` ("the return URL") — honour it, or a shared deep link
    // (/tickets?ticket=..., a bookmarked page, a session that expired mid-work)
    // silently loses the user's place and dumps them on the dashboard.
    // Login.js's OTP step navigations carry `from` forward in their own state.
    const from = location.state?.from;
    const destination = from
        ? `${from.pathname || '/'}${from.search || ''}${from.hash || ''}`
        : '/';

    const handleLoginSuccess = (loggedInUser, token) => {
        login(loggedInUser, token);
        // replace: the login entry must not stay in history, or the browser
        // back button lands an authenticated user on a login form.
        navigate(destination, { replace: true });
    };

    // Already signed in (e.g. back button after login) — nothing to render here.
    if (user) return <Navigate to={destination} replace />;

    return <Login onLoginSuccess={handleLoginSuccess} />;
}

function AppContent() {
    const { user, logout } = useAuth();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [selection, setSelection] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [showWarning, setShowWarning] = useState(false);
    const [showBackToStep1Warning, setShowBackToStep1Warning] = useState(false);
    const [processedFilesCount, setProcessedFilesCount] = useState(0);
    const [focusedFileIndex, setFocusedFileIndex] = useState(-1);
    const {
        timesheets,
        updateTimesheet,
        toggleChecked,
        removeTimesheet
    } = useTimesheetData();
    const [isExtractingTimesheet, setIsExtractingTimesheet] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [showFullScreenImage, setShowFullScreenImage] = useState(false);
    const [showEditAllModal, setShowEditAllModal] = useState(false);

    const steps = [
        { number: 1, title: 'העלאת קבצים' },
        { number: 2, title: 'עיבוד נתונים' },
        { number: 3, title: 'ייצוא' }
    ];

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = 'נתונים לא נשמרו! האם אתה בטוח שברצונך לעזוב?';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle keyboard navigation in step 2
            if (currentStep !== 2 || uploadedFiles.length === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setFocusedFileIndex(prev => {
                        const newIndex = prev < uploadedFiles.length - 1 ? prev + 1 : prev;
                        const file = uploadedFiles[newIndex];
                        if (file) handleFileSelect(file);
                        return newIndex;
                    });
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setFocusedFileIndex(prev => {
                        const newIndex = prev > 0 ? prev - 1 : prev;
                        const file = uploadedFiles[newIndex];
                        if (file) handleFileSelect(file);
                        return newIndex;
                    });
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (focusedFileIndex >= 0 && focusedFileIndex < uploadedFiles.length) {
                        const file = uploadedFiles[focusedFileIndex];
                        handleFileSelect(file);
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentStep, uploadedFiles, focusedFileIndex]);

    const extractTimesheetData = async (file) => {
        if (!file) return;

        try {
            const response = await axiosInstance.post('/table/extract-timesheet', {
                filename: file.filename,
            });

            if (response.data.error) {
                throw new Error(response.data.message || 'שגיאה בחילוץ נתוני גיליון הנוכחות');
            }

            const result = response.data.result;
            // Get the original filename and remove the extension
            const originalName = file.originalFile?.name || file.filename;
            const nameWithoutExt = removeFileExtension(originalName);

            const transformedData = {
                employeeName: result.employee_name || '',
                passportId: nameWithoutExt, // Use filename without extension
                month: new Date().getMonth() + 1,
                year: new Date().getFullYear(),
                rows: result.work_hours.map(workHour => ({
                    day: workHour.day,
                    startHour: workHour.start_hour || null,
                    endHour: workHour.end_hour || null,
                    isEmpty: !workHour.start_hour && !workHour.end_hour
                }))
            };

            const timesheet = new Timesheet(transformedData);
            updateTimesheet(file.filename, timesheet);
            setProcessedFilesCount(prev => prev + 1);

            // Count how many files have data from the server
            const filesWithData = Object.keys(timesheets).length;
            const totalFiles = uploadedFiles.length;

        } catch (err) {
            setError(err.message || 'אירעה שגיאה במהלך חילוץ נתוני גיליון הנוכחות');
            throw err;
        }
    };

    const handleFileUpload = async (file) => {
        const existingFileIndex = uploadedFiles.findIndex(f => f.filename === file.filename);

        if (existingFileIndex === -1) {
            setUploadedFiles(prevFiles => [...prevFiles, file]);
        } else {
            const updatedFiles = [...uploadedFiles];
            updatedFiles[existingFileIndex] = file;
            setUploadedFiles(updatedFiles);
        }

        setSelectedFile(file);
    };

    const handleNext = async () => {
        if (currentStep < steps.length) {
            if (currentStep === 1) {
                // Start processing files when moving from step 1 to 2
                setIsExtractingTimesheet(true);
                setError(null);
                setProcessedFilesCount(0);
                
                try {
                    // Process all files in parallel
                    await Promise.all(uploadedFiles.map(extractTimesheetData));
                    setCurrentStep(currentStep + 1);
                } catch (err) {
                    setError(err.message || 'אירעה שגיאה במהלך חילוץ נתוני גיליון הנוכחות');
                } finally {
                    setIsExtractingTimesheet(false);
                }
            } else if (currentStep === 2) {
                const allFilesChecked = uploadedFiles.every(file => timesheets[file.filename]?.isChecked);
                
                if (!allFilesChecked) {
                    setShowWarning(true);
                    return;
                }
                setCurrentStep(currentStep + 1);
            } else {
                setCurrentStep(currentStep + 1);
            }
            setError(null);
        }
    };

    const handleFileSelect = (file) => {
        setSelectedFile(file);
    };

    const handleRemoveFile = (fileToRemove) => {
        setUploadedFiles(prevFiles => prevFiles.filter(file => file.filename !== fileToRemove.filename));
        removeTimesheet(fileToRemove.filename);
        
        if (selectedFile && selectedFile.filename === fileToRemove.filename) {
            setSelectedFile(null);
        }
    };

    const handleProceedAnyway = () => {
        setShowWarning(false);
        setCurrentStep(currentStep + 1);
    };

    const handleBack = () => {
        if (currentStep > 1) {
            if (currentStep === 3) {
                setShowBackToStep1Warning(true);
            } else {
                setCurrentStep(currentStep - 1);
            }
        }
    };

    const handleBackToStep1 = () => {
        setShowBackToStep1Warning(false);
        setCurrentStep(1);
        setUploadedFiles([]);
        setSelectedFile(null);
        setError(null);
        setSelection(null);
        setProcessedFilesCount(0);
        setFocusedFileIndex(-1);
        // Clear all timesheets
        Object.keys(timesheets).forEach(filename => {
            removeTimesheet(filename);
        });
    };

    const handleProceedBackToStep1 = () => {
        handleBackToStep1();
    };

    const handleCancelBackToStep1 = () => {
        setShowBackToStep1Warning(false);
    };

    const getCurrentTimesheets = () => {
        return uploadedFiles
            .map(file => timesheets[file.filename])
            .filter(timesheet => timesheet && timesheet.isChecked);
    };

    const calculateSummaryStats = (timesheets) => {
        const stats = {
            totalFiles: timesheets.length,
            totalDays: 0,
            totalHours: 0,
            averageHoursPerDay: 0,
            incompleteEntries: 0,
            warnings: []
        };

        timesheets.forEach(timesheet => {
            if (timesheet && timesheet.rows) {
                timesheet.rows.forEach(row => {
                    if (row.startHour && row.endHour) {
                        const hours = calculateHours(row.startHour, row.endHour);
                        stats.totalHours += hours;
                        stats.totalDays++;
                    } else if (row.startHour || row.endHour) {
                        stats.incompleteEntries++;
                    }
                });
            }
        });

        stats.averageHoursPerDay = stats.totalDays > 0 ? (stats.totalHours / stats.totalDays).toFixed(2) : 0;

        // Add warnings
        if (stats.incompleteEntries > 0) {
            stats.warnings.push(`${stats.incompleteEntries} entries with incomplete data`);
        }

        return stats;
    };

    const getCurrentTimesheetWarnings = () => {
        const warnings = [];
        getCurrentTimesheets().forEach(timesheet => {
            if (timesheet && timesheet.rows) {
                const rowWarnings = validateRows(timesheet.rows);
                warnings.push(...rowWarnings.map(warning => `${timesheet.employeeName}: ${warning}`));
            }
        });
        return warnings;
    };

    const SummaryModal = ({ show, onHide, timesheets }) => {
        const stats = calculateSummaryStats(timesheets);
        const warnings = getCurrentTimesheetWarnings();

        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>סיכום נתונים</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>סטטיסטיקות כלליות</h5>
                                    <p><strong>קבצים:</strong> {stats.totalFiles}</p>
                                    <p><strong>ימים:</strong> {stats.totalDays}</p>
                                    <p><strong>שעות כולל:</strong> {stats.totalHours.toFixed(2)}</p>
                                    <p><strong>ממוצע ליום:</strong> {stats.averageHoursPerDay}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>אזהרות</h5>
                                    {warnings.length > 0 ? (
                                        <ul className="list-unstyled">
                                            {warnings.map((warning, index) => (
                                                <li key={index} className="text-warning">
                                                    <FaTimes className="ms-2" />
                                                    {warning}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-success">
                                            <FaCheck className="ms-2" />
                                            אין אזהרות
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        סגור
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="step-content">
                        <h2 className="step-title">שלב 1: העלאת קבצים</h2>
                        <p className="step-description">
                            העלה תמונות של גיליונות נוכחות או קבצי PDF
                        </p>
                        <FileUpload onFileUpload={handleFileUpload} />
                        
                        {uploadedFiles.length > 0 && (
                            <div className="mt-4">
                                <h4>קבצים שהועלו ({uploadedFiles.length})</h4>
                                <div className="uploaded-files">
                                    {uploadedFiles.map((file, index) => (
                                        <Card key={file.filename} className="mb-2">
                                            <Card.Body className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong>{removeFileExtension(file.filename)}</strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        גודל: {(file.size / 1024).toFixed(1)} KB
                                                    </small>
                                                </div>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveFile(file)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 2:
                return (
                    <div className="step-content">
                        <h2 className="step-title">שלב 2: עיבוד נתונים</h2>
                        <p className="step-description">
                            בחר קובץ לעיבוד ובדוק את הנתונים שחולצו
                        </p>
                        
                        {isExtractingTimesheet && (
                            <div className="text-center mb-4">
                                <LoadingIndicator />
                                <p>מעבד קבצים... {processedFilesCount}/{uploadedFiles.length}</p>
                            </div>
                        )}
                        
                        <Row>
                            <Col md={4}>
                                <div className="file-list">
                                    <h5>קבצים</h5>
                                    {uploadedFiles.map((file, index) => (
                                        <div
                                            key={file.filename}
                                            className={`file-item ${selectedFile?.filename === file.filename ? 'active' : ''} ${focusedFileIndex === index ? 'focused' : ''}`}
                                            onClick={() => handleFileSelect(file)}
                                        >
                                            <div className="file-info">
                                                <strong>{removeFileExtension(file.filename)}</strong>
                                                <br />
                                                <small className="text-muted">
                                                    {timesheets[file.filename] ? 'עובד' : 'לא עובד'}
                                                </small>
                                            </div>
                                            <div className="file-actions">
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveFile(file);
                                                    }}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                            <Col md={8}>
                                {selectedFile ? (
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5>תצוגה מקדימה: {removeFileExtension(selectedFile.filename)}</h5>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => setShowFullScreenImage(true)}
                                            >
                                                <FaExpand /> תצוגה מלאה
                                            </Button>
                                        </div>
                                        
                                        <ImageViewer imageUrl={selectedFile.path} />
                                        
                                        {timesheets[selectedFile.filename] && (
                                            <div className="mt-3">
                                                <ResultsTable
                                                    timesheet={timesheets[selectedFile.filename]}
                                                    onUpdate={(updatedTimesheet) => {
                                                        updateTimesheet(selectedFile.filename, updatedTimesheet);
                                                    }}
                                                    onToggleChecked={() => {
                                                        toggleChecked(selectedFile.filename);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted">
                                        <FaImage size={48} className="mb-3" />
                                        <p>בחר קובץ מהרשימה להצגת תצוגה מקדימה</p>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                );
            case 3:
                return (
                    <div className="step-content">
                        <h2 className="step-title">שלב 3: ייצוא</h2>
                        <p className="step-description">
                            ייצא את הנתונים המעובדים לקובץ Excel
                        </p>
                        
                        <Card className="mb-4">
                            <Card.Body>
                                <h5>סיכום נתונים</h5>
                                <p>
                                    <strong>קבצים:</strong> {getCurrentTimesheets().length}
                                </p>
                                <Button
                                    variant="primary"
                                    onClick={() => setShowSummaryModal(true)}
                                >
                                    <FaChartBar className="ms-2" />
                                    הצג סיכום מפורט
                                </Button>
                            </Card.Body>
                        </Card>
                        
                        <div className="export-options">
                            <h5>אפשרויות ייצוא</h5>
                            <Button
                                variant="success"
                                size="lg"
                                onClick={async () => {
                                    const timesheets = getCurrentTimesheets();
                                    // Dynamic import for code splitting - xlsx library is large
                                    const { exportTimesheetsToExcel } = await import('./utils/excelUtils');
                                    exportTimesheetsToExcel(timesheets);
                                }}
                            >
                                <FaDownload className="ms-2" />
                                ייצא ל-Excel
                            </Button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="app-container" dir="rtl">
            {/* Navigation Bar */}
            <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
                <Container>
                    <Navbar.Brand href="#home">
                        <FaTable className="ms-2" />
                        Timesheet OCR
                    </Navbar.Brand>
                    
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                        <Nav className="ms-auto">
                            <Nav.Link href="#home">
                                <FaHome className="ms-1" />
                                דף הבית
                            </Nav.Link>
                        </Nav>
                        
                        <Nav>
                            <Dropdown align="end">
                                <Dropdown.Toggle variant="dark" id="dropdown-user">
                                    <FaUserCircle className="ms-2" />
                                    {user?.firstName ? `${user.firstName} ${user.lastName}` : user?.username}
                                </Dropdown.Toggle>
                                
                                <Dropdown.Menu>
                                    <Dropdown.Header>
                                        <div>
                                            <strong>{user?.firstName ? `${user.firstName} ${user.lastName}` : user?.username}</strong>
                                            <br />
                                            <small className="text-muted">{user?.email}</small>
                                        </div>
                                    </Dropdown.Header>
                                    <Dropdown.Divider />
                                    <Dropdown.Item href="#profile">
                                        <FaUserCircle className="ms-2" />
                                        פרופיל
                                    </Dropdown.Item>
                                    <Dropdown.Item href="#settings">
                                        <FaCog className="ms-2" />
                                        הגדרות
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={logout}>
                                        <FaSignOutAlt className="ms-2" />
                                        התנתק
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>

            <Container>
                {/* Progress Steps */}
                <div className="progress-steps mb-4">
                    <div className="steps-container">
                        {steps.map((step, index) => (
                            <div
                                key={step.number}
                                className={`step ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}
                            >
                                <div className="step-number">{step.number}</div>
                                <div className="step-title">{step.title}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <ErrorDisplay error={error} onClose={() => setError(null)} />
                )}

                {/* Main Content */}
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="navigation-buttons mt-4">
                    <div className="d-flex justify-content-between">
                        <Button
                            variant="outline-secondary"
                            onClick={handleBack}
                            disabled={currentStep === 1}
                        >
                            <FaArrowRight className="ms-2" />
                            חזור
                        </Button>
                        
                        {currentStep < steps.length && (
                            <Button
                                variant="primary"
                                onClick={handleNext}
                                disabled={currentStep === 1 && uploadedFiles.length === 0}
                            >
                                המשך
                                <FaArrowLeft className="ms-2" />
                            </Button>
                        )}
                    </div>
                </div>
            </Container>

            {/* Modals */}
            <SummaryModal
                show={showSummaryModal}
                onHide={() => setShowSummaryModal(false)}
                timesheets={getCurrentTimesheets()}
            />

            <FullScreenImageModal
                show={showFullScreenImage}
                onHide={() => setShowFullScreenImage(false)}
                imageUrl={selectedFile?.path}
                timesheet={selectedFile ? timesheets[selectedFile.filename] : null}
                onUpdate={(updatedTimesheet) => {
                    if (selectedFile) {
                        updateTimesheet(selectedFile.filename, updatedTimesheet);
                    }
                }}
            />

            <EditAllHoursModal
                show={showEditAllModal}
                onHide={() => setShowEditAllModal(false)}
                timesheets={getCurrentTimesheets()}
                onUpdate={(filename, updatedTimesheet) => {
                    updateTimesheet(filename, updatedTimesheet);
                }}
            />

            {/* Warning Modals */}
            <Modal show={showWarning} onHide={() => setShowWarning(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>אזהרה</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>לא כל הקבצים מסומנים. האם אתה בטוח שברצונך להמשיך?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowWarning(false)}>
                        ביטול
                    </Button>
                    <Button variant="primary" onClick={handleProceedAnyway}>
                        המשך בכל זאת
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showBackToStep1Warning} onHide={() => setShowBackToStep1Warning(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>אזהרה</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>חזרה לשלב 1 תמחק את כל הנתונים המעובדים. האם אתה בטוח?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelBackToStep1}>
                        ביטול
                    </Button>
                    <Button variant="danger" onClick={handleProceedBackToStep1}>
                        המשך בכל זאת
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Loading Overlay */}
            {isExtractingTimesheet && <FullScreenLoader />}
        </div>
    );
}

export default App;